import { useEffect, useRef, useCallback } from "react";
import { useNavGuard } from "../components/NavigationGuard";

export function useUnsavedGuard({ isDirty, draftKey, draftData, onSaveDraft, onDiscard }) {
  const ctx = useNavGuard();
  const guardKeyRef = useRef(`guard-${Math.random().toString(36).slice(2, 10)}`);
  
  // Store callbacks in refs to avoid re-registering on every render
  const onSaveDraftRef = useRef(onSaveDraft);
  const onDiscardRef = useRef(onDiscard);
  const draftKeyRef = useRef(draftKey);
  
  // Update refs when callbacks change
  useEffect(() => {
    onSaveDraftRef.current = onSaveDraft;
    onDiscardRef.current = onDiscard;
    draftKeyRef.current = draftKey;
  });

  // Auto-save draft
  useEffect(() => {
    if (!draftKey) return;
    if (isDirty && draftData) {
      try { localStorage.setItem(draftKey, JSON.stringify({ data: draftData, savedAt: Date.now() })); } catch { /* ignore */ }
    }
  }, [isDirty, draftKey, draftData]);

  // Register with global provider so any guarded navigation is intercepted
  useEffect(() => {
    if (!ctx) return;
    const key = guardKeyRef.current;
    const saveWrapper = () => { if (onSaveDraftRef.current) onSaveDraftRef.current(); };
    const discardWrapper = () => {
      if (draftKeyRef.current) { try { localStorage.removeItem(draftKeyRef.current); } catch { /* ignore */ } }
      if (onDiscardRef.current) onDiscardRef.current();
    };
    ctx.register(key, { isDirty, onSaveDraft: saveWrapper, onDiscard: discardWrapper });
    return () => ctx.unregister(key);
  }, [ctx, isDirty]);

  /** Use this for the editor's own Close / Cancel button. Shows the global modal. */
  const requestClose = useCallback((afterClose) => {
    if (ctx) {
      ctx.promptAction(afterClose || (() => {}));
    } else {
      afterClose && afterClose();
    }
  }, [ctx]);

  return { requestClose, promptOpen: false, confirm: () => {} };
}

export function loadDraft(draftKey, maxAgeDays = 7) {
  try {
    const raw = localStorage.getItem(draftKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.savedAt) return null;
    const age = (Date.now() - parsed.savedAt) / (1000 * 60 * 60 * 24);
    if (age > maxAgeDays) { localStorage.removeItem(draftKey); return null; }
    return parsed.data;
  } catch { return null; }
}

export function clearDraft(draftKey) {
  try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
}
